import type { ReactElement } from 'react';
import {
  BriefcaseBusiness,
  Boxes,
  Container,
  Database,
  Fingerprint,
  GitCommitHorizontal,
  KeyRound,
  LayoutTemplate,
  Network,
  ScrollText,
  ShieldCheck,
  SlidersHorizontal,
  Users,
} from 'lucide-react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@/auth/usePermissions';
import { PageWithSidebar } from '@/components/shell/PageWithSidebar';
import { useSetBreadcrumbs } from '@/components/shell/ShellContext';
import { Sidebar, type SidebarGroup, type SidebarItem } from '@/components/shell/Sidebar';

/**
 * Platform console section shell (PTV2-1021). Mirrors `AdminPage`: a left
 * sidebar plus a nested-route outlet. Tenants is always visible to any
 * platform user; Tenant templates and Cluster grants are PLATFORM_ADMIN-only
 * (`managePlatform`). The whole section is gated upstream in `routes/index.tsx`
 * via `<RequirePermission perm="openPlatform">`.
 */
export function PlatformPage(): ReactElement {
  const { t } = useTranslation();
  const { canManagePlatform } = usePermissions();

  const items: SidebarItem[] = [
    {
      key: 'tenants',
      label: t('routes.platform.sidebar.tenants'),
      to: '/platform/tenants',
      icon: <Boxes className="h-4 w-4" aria-hidden="true" />,
    },
    {
      key: 'users',
      label: t('routes.platform.sidebar.users'),
      to: '/platform/users',
      icon: <Users className="h-4 w-4" aria-hidden="true" />,
    },
    {
      key: 'jobs',
      label: t('routes.platform.sidebar.jobs'),
      to: '/platform/jobs',
      icon: <BriefcaseBusiness className="h-4 w-4" aria-hidden="true" />,
    },
  ];

  if (canManagePlatform) {
    items.push(
      {
        key: 'templates',
        label: t('routes.platform.sidebar.templates'),
        to: '/platform/templates',
        icon: <LayoutTemplate className="h-4 w-4" aria-hidden="true" />,
      },
      {
        key: 'grants',
        label: t('routes.platform.sidebar.grants'),
        to: '/platform/grants',
        icon: <KeyRound className="h-4 w-4" aria-hidden="true" />,
      },
      {
        key: 'configs',
        label: t('routes.platform.sidebar.configs'),
        to: '/platform/configs',
        icon: <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />,
      },
    );
  }

  // PTV2-1383: OAuth credentials + auth providers are global-plane (cluster-wide)
  // identity config gated on platform:MANAGE — the chicken-and-egg is real (you
  // can't reach a tenant until its provider exists), so they live in the global
  // console, not tenant admin. Hidden for platform users without the tier.
  const accessGroup: readonly SidebarGroup[] = canManagePlatform
    ? [
        {
          key: 'access',
          heading: t('routes.platform.sidebar.accessHeading'),
          items: [
            {
              key: 'oidc-credentials',
              label: t('routes.platform.sidebar.oidcCredentials'),
              to: '/platform/oidc-credentials',
              icon: <KeyRound className="h-4 w-4" aria-hidden="true" />,
            },
            {
              key: 'auth-providers',
              label: t('routes.platform.sidebar.authProviders'),
              to: '/platform/auth-providers',
              icon: <ShieldCheck className="h-4 w-4" aria-hidden="true" />,
            },
            {
              key: 'federated-identities',
              label: t('routes.platform.sidebar.federatedIdentities'),
              to: '/platform/federated-identities',
              icon: <Fingerprint className="h-4 w-4" aria-hidden="true" />,
            },
            {
              key: 'dockerhub',
              label: t('routes.platform.sidebar.dockerHub'),
              to: '/platform/dockerhub',
              icon: <Container className="h-4 w-4" aria-hidden="true" />,
            },
          ],
        },
      ]
    : [];

  const groups: readonly SidebarGroup[] = [
    {
      key: 'platform',
      heading: t('routes.platform.sidebar.heading'),
      items,
    },
    ...accessGroup,
    // PTV2-1383: cluster-wide infra tabs, moved here from tenant admin.
    {
      key: 'cluster',
      heading: t('routes.platform.sidebar.clusterHeading'),
      items: [
        {
          key: 'discovery',
          label: t('routes.platform.sidebar.discovery'),
          to: '/platform/cluster',
          icon: <Network className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 'build-info',
          label: t('routes.platform.sidebar.buildInfo'),
          to: '/platform/build-info',
          icon: <GitCommitHorizontal className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 'logs',
          label: t('routes.platform.sidebar.logs'),
          to: '/platform/logs',
          icon: <ScrollText className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 'database',
          label: t('routes.platform.sidebar.database'),
          to: '/platform/database',
          icon: <Database className="h-4 w-4" aria-hidden="true" />,
        },
      ],
    },
  ];

  // PTV2-1383: top-bar breadcrumbs "Administration › <section>", reusing the sidebar's own labels so
  // the trail always matches the active nav item.
  const { pathname } = useLocation();
  const activeSection = groups
    .flatMap((group) => group.items)
    .find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
  useSetBreadcrumbs([
    { label: t('shell.topNav.admin'), to: '/platform' },
    ...(activeSection === undefined ? [] : [{ label: activeSection.label }]),
  ]);

  return (
    <PageWithSidebar
      sidebar={<Sidebar groups={groups} />}
      mainAriaLabel={t('routes.platform.heading')}
    >
      <Outlet />
    </PageWithSidebar>
  );
}
